import Header from "../../Layouts/Components/Header";
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Typography,
  Button,
  Divider,
  Modal,
  Box,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  TextField,
  Autocomplete,
  Checkbox,
} from "@mui/material";
import KeyIcon from "@mui/icons-material/Key";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CustomButton from "../shared/CustomButton";
import { useDispatch, useSelector } from "react-redux";
import {
  getLicense,
  resetLicense,
  updateLicense,
  changeLicense,
  getLicenseHistory,
  getModules,
} from "./state/actions";
import getModuleIcon from "../../utils/getModuleIcon";
import ScreenLoader from "../shared/ScreenLoader";
import useSnackBar from "../Hooks/useSnackBar";
import { calculateLeftDays } from "../../utils/helperFunctions";
import ViewHistoryModal from "./ViewHistoryModal";
import { useKeycloak } from "@react-keycloak/web";

const License = () => {
  const dispatch = useDispatch();
  const {
    license,
    activeModules,
    deactivatedModules,
    otherModules,
    allowedModules,
    processing,
    loadingLicense,
    loadingLicenseHistory,
    licenseHistory,
    modules,
  } = useSelector((state) => state.license);

  const { keycloak } = useKeycloak();
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openChangeModal, setOpenChangeModal] = useState(false);

  const [newKeyTouched, setNewKeyTouched] = useState(false);

  const [isViewHistoryModalOpen, setIsViewHistoryModalOpen] = useState(false);

  const [modulesTouched, setModulesTouched] = useState(false);

  const { success, error, snackbar } = useSnackBar();

  const Navigate = useNavigate();

  const loadLicense = useCallback(
    () => dispatch(getLicense(keycloak.tokenParsed["license-key"])),
    [keycloak, dispatch]
  );

  const loadModules = useCallback(
    () => dispatch(getModules(error)),
    [dispatch, error]
  );
  const reset = useCallback(() => dispatch(resetLicense()), [dispatch]);

  const handleOpenUpdateModal = () => {
    setOpenUpdateModal(true);
  };
  const handleCloseUpdateModal = () => {
    setOpenUpdateModal(false);
    changeModulesForm.resetForm();
  };

  const handleOpenChangeModal = () => setOpenChangeModal(true);
  const handleCloseChangeModal = () => setOpenChangeModal(false);

  const handleKeyChange = (e) => {
    if (!newKeyTouched) setNewKeyTouched(true);
    formik.handleChange(e);
  };

  const handleOpenViewHistoryModal = () => {
    dispatch(getLicenseHistory(license.uniqueId, error));
    setIsViewHistoryModalOpen(true);
  };

  const handleChangeModules = (e, newValue) => {
    setModulesTouched(true);
    changeModulesForm.setFieldValue("modules", newValue);
  };

  const updateModalstyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 480,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
  };

  const changeModalstyle = {
    position: "absolute",
    top: "6%",
    left: "25%",
    width: "50%",
    height: "480px",
    bgcolor: "#F8FAFB",
    boxShadow: 24,
    borderRadius: "6px",
  };

  //change license key Form validation
  const validationSchema = yup.object().shape({
    newKey: yup.string().required("This field is mandatory"),
  });

  //change modules Form validation
  const modulesValidationSchema = yup.object().shape({
    modules: yup
      .array()
      .of(yup.object())
      .min(1, "This field is mandatory")
      .required("This field is mandatory"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      newKey: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const data = { key: values.newKey };
      dispatch(
        changeLicense(
          license?.uniqueId || keycloak.tokenParsed["license-key"],
          data,
          success,
          error
        )
      ).then(() => {
        dispatch(getLicense(keycloak.tokenParsed["license-key"]));
        setNewKeyTouched(false);
        setOpenChangeModal(false);
      });
    },
  });

  const changeModulesForm = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      modules: [],
    },
    validationSchema: modulesValidationSchema,
    onSubmit: (values) => {
      const modules = [...values.modules.map((module) => module.id)];
      dispatch(updateLicense(license.uniqueId, modules, success, error)).then(
        () => handleCloseUpdateModal()
      );
    },
  });

  const getChangeModulesDropdown = (currentModules, allModules) => {
    const currentModulesHashMap = {};
    const dropdown = [];
    currentModules?.forEach((module) => {
      currentModulesHashMap[module.module.id] = module;
    });
    allModules?.forEach((module) => {
      if (!currentModulesHashMap[module.id]) dropdown.push(module);
    });

    return dropdown;
  };

  useEffect(() => {
    loadModules();
    return () => {
      reset();
    };
  }, [loadModules, reset]);

  useEffect(() => {
    loadLicense();
  }, [loadLicense, modules]);

  return (
    <div>
      {snackbar}
      <Header headLine="License" />
      {loadingLicense ? (
        <ScreenLoader />
      ) : (
        <>
          <Grid
            container
            justifyContent="center"
            sx={{
              background: "#ffffff",
              borderRadius: "6px",
              border: "1px solid #E7E9EF",
              margin: "30px",
              padding: "20px",
              width: "70%",
            }}
            direction="column"
          >
            {!license ? (
              <Grid item xs={4}>
                <Button
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#1492AF",
                    fontSize: ".875rem",
                  }}
                  startIcon={<KeyIcon />}
                  onClick={handleOpenChangeModal}
                >
                  Activate License
                </Button>
              </Grid>
            ) : (
              <>
                <Typography
                  sx={{
                    color: "#050D2E",
                    fontSize: "1rem",
                    fontWeight: 550,
                    lineHeight: "21px",
                    marginBottom: "15px",
                  }}
                >
                  License Overview
                </Typography>
                {/*INFO */}
                <Grid item container direction="column">
                  {/*Name */}
                  <Grid item container>
                    <Grid item xs={2}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          lineHeight: "21px",
                        }}
                      >
                        Licensed to:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          fontWeight: 550,
                          lineHeight: "21px",
                        }}
                      >
                        {license.bankName} {license.bankBic}
                      </Typography>
                    </Grid>
                  </Grid>
                  {/*Key */}
                  <Grid item container>
                    <Grid item xs={2}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          lineHeight: "21px",
                        }}
                      >
                        Licensed Key:
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          fontWeight: 550,
                          lineHeight: "21px",
                        }}
                      >
                        {license.keySimplified}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="text"
                        sx={{
                          textTransform: "none",
                          padding: "0px",
                          color: "#007AFF",
                          fontSize: ".875rem",
                        }}
                        startIcon={<KeyIcon />}
                        onClick={handleOpenChangeModal}
                      >
                        Change Key
                      </Button>
                    </Grid>
                  </Grid>
                  {/*Users */}
                  <Grid item container>
                    <Grid item xs={2}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          lineHeight: "21px",
                        }}
                      >
                        Users:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          fontWeight: 550,
                          lineHeight: "21px",
                        }}
                      >
                        {license.allowedUsers}
                      </Typography>
                    </Grid>
                  </Grid>
                  {/*Messages */}
                  <Grid item container>
                    <Grid item xs={2}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          lineHeight: "21px",
                        }}
                      >
                        Messages/Day:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          fontWeight: 550,
                          lineHeight: "21px",
                        }}
                      >
                        {license.messages}
                      </Typography>
                    </Grid>
                  </Grid>
                  {/*Renewal */}
                  <Grid item container>
                    <Grid item xs={2}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          lineHeight: "21px",
                        }}
                      >
                        License Renewal:
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        sx={{
                          color: "#050D2E",
                          fontSize: ".875rem",
                          lineHeight: "21px",
                          fontWeight: 550,
                        }}
                      >
                        {`${new Date(license.expiryDate).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        )} (${calculateLeftDays(
                          new Date(license?.creationDate),
                          new Date(license?.expiryDate)
                        )} Days)
                    `}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Button
                      startIcon={<HistoryIcon sx={{ width: "14px" }} />}
                      sx={{
                        textTransform: "none",
                        color: "#007AFF",
                        marginTop: "10px",
                      }}
                      onClick={handleOpenViewHistoryModal}
                    >
                      View License History
                    </Button>
                  </Grid>
                </Grid>
                <Divider sx={{ marginTop: "30px", marginBottom: "30px" }} />
                {/*Activated Modules */}
                <Typography
                  sx={{
                    color: "#050D2E",
                    fontSize: "1rem",
                    fontWeight: 550,
                    lineHeight: "21px",
                    marginBottom: "15px",
                  }}
                >
                  Activated Modules ({activeModules.length})
                </Typography>
                <Grid item container columnSpacing={2} pb={4}>
                  {activeModules.map((module) => (
                    <Grid
                      key={module.module.id}
                      item
                      container
                      direction="column"
                      xs={2}
                      alignItems="center"
                    >
                      <img
                        src={getModuleIcon(module.module.id)}
                        loading="lazy"
                        alt={`${module.module.name}`}
                        style={{
                          width: "44px",
                          height: "44px",
                          backgroundColor: "#f6f6f6",
                        }}
                      />
                      <Grid item>
                        <Typography
                          sx={{
                            color: "#050D2E",
                            fontSize: ".938rem",
                            lineHeight: "21px",
                            opacity: 0.95,
                            mt: 1,
                            textAlign: "center",
                          }}
                        >
                          {module.module.name}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <Grid item>
                  <CustomButton
                    text="Customize Modules"
                    processing={false}
                    type="dark"
                    action={() => Navigate("/license/modules")}
                  />
                </Grid>
                {/*Activated Modules */}
                {deactivatedModules.length ? (
                  <>
                    <Divider sx={{ marginTop: "30px", marginBottom: "30px" }} />
                    <Typography
                      sx={{
                        color: "#050D2E",
                        fontSize: "1rem",
                        fontWeight: 550,
                        lineHeight: "21px",
                        marginBottom: "15px",
                      }}
                    >
                      Deactivated Modules ({deactivatedModules.length})
                    </Typography>

                    <Grid
                      item
                      container
                      rowSpacing={4}
                      columnSpacing={8}
                      p={2}
                      pt={0}
                    >
                      {deactivatedModules.map((module) => (
                        <Grid
                          key={module.module.id}
                          item
                          container
                          direction="column"
                          xs={2}
                          alignItems="center"
                        >
                          <img
                            src={getModuleIcon(module.module.id)}
                            loading="lazy"
                            alt={`${module.module.name}`}
                            style={{
                              width: "44px",
                              height: "44px",
                              backgroundColor: "#f6f6f6",
                            }}
                          />
                          <Grid item>
                            <Typography
                              sx={{
                                color: "#050D2E",
                                fontSize: ".938rem",
                                lineHeight: "21px",
                                opacity: 0.95,
                                mt: 1,
                                textAlign: "center",
                              }}
                            >
                              {module.module.name}
                            </Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                ) : null}

                {/*Other Modules */}

                <Divider sx={{ marginTop: "30px", marginBottom: "30px" }} />

                {otherModules.length ? (
                  <>
                    <Typography
                      sx={{
                        color: "#050D2E",
                        fontSize: "1rem",
                        fontWeight: 550,
                        lineHeight: "21px",
                        marginBottom: "15px",
                      }}
                    >
                      Other Modules ({otherModules?.length})
                    </Typography>
                    <Grid
                      item
                      container
                      rowSpacing={4}
                      columnSpacing={8}
                      p={2}
                      pt={0}
                    >
                      {otherModules.map((module) => (
                        <Grid
                          key={module.module.id}
                          item
                          container
                          justifyContent="center"
                          alignItems="center"
                          xs={6}
                        >
                          <Grid item xs={2}>
                            <img
                              src={getModuleIcon(module.module.id)}
                              loading="lazy"
                              alt={`${module.module.name}`}
                              style={{
                                width: "28px",
                                height: "28px",
                                backgroundColor: "#f6f6f6",
                              }}
                            />
                          </Grid>
                          <Grid item xs={10}>
                            <Typography
                              sx={{
                                color: "#050D2E",
                                fontSize: ".938rem",
                                lineHeight: "18px",
                                opacity: 0.95,
                                fontWeight: 500,
                              }}
                            >
                              {module.module.name}
                            </Typography>
                            <Typography
                              sx={{
                                color: "#50566E",
                                fontSize: ".813rem",
                                lineHeight: "15px",
                                opacity: 0.75,
                              }}
                            >
                              {module.module.description}
                            </Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                ) : null}

                {getChangeModulesDropdown(allowedModules, modules).length ? (
                  <Grid item xs={12}>
                    <CustomButton
                      text="Change Modules"
                      processing={false}
                      type="update"
                      action={handleOpenUpdateModal}
                    />
                  </Grid>
                ) : null}
              </>
            )}
          </Grid>
          {/*Change mudules Modal  */}
          <Modal
            open={openUpdateModal}
            onClose={handleCloseUpdateModal}
            aria-labelledby="modal-Update-modules"
            aria-describedby="modal-Update-modules"
          >
            <Box sx={updateModalstyle}>
              {modules ? (
                <>
                  <Typography
                    id="modal-Update-modules"
                    sx={{
                      color: "#050D2E",
                      fontSize: "1.25rem",
                      fontWeight: 550,
                      lineHeight: "24px",
                    }}
                  >
                    Module Change Request
                  </Typography>
                  <Typography
                    id="modal-Update-modules"
                    sx={{
                      color: "#50566E",
                      fontSize: "1rem",
                      lineHeight: "24px",
                      mt: 2,
                    }}
                  >
                    Would you like to renew your license and update the modules
                    you're using? If yes, one of our agents will contact you to
                    arrange modifications. Please make sure that your contact
                    info is updated.
                  </Typography>
                  <Box sx={{ height: "20px" }}></Box>
                  <Typography
                    sx={{
                      color: "#050D2E",
                      fontSize: "14px",
                      fontWeight: 550,
                      lineHeight: "16px",
                      mb: 1,
                    }}
                  >
                    Select Modules
                  </Typography>
                  <Autocomplete
                    size="small"
                    disableClearable={true}
                    id="bank-modules-options"
                    placeholder="SELECT MODULES"
                    multiple
                    limitTags={3}
                    groupBy={() => "SELECT MODULES"}
                    value={changeModulesForm.values.modules}
                    onChange={handleChangeModules}
                    options={getChangeModulesDropdown(allowedModules, modules)}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderOption={(props, option, { selected }) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{ paddingLeft: "0px", marginLeft: "0px" }}
                      >
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          sx={{
                            height: "28px",
                            width: "32px",
                          }}
                          checked={selected}
                        />
                        {option.name}
                      </Box>
                    )}
                    sx={{ width: "100%" }}
                    renderInput={(params) => (
                      <TextField
                        sx={{
                          ".MuiFormLabel-root": { color: "#50566E" },
                          "& .MuiInputBase-root": {
                            backgroundColor: "#ffffff",
                            borderRadius: "4px",
                            color: "#1C2238",
                            fontSize: ".875rem",
                            "& fieldset": {
                              borderColor: "#D3D8E3",
                            },
                            "&:hover fieldset": {
                              borderColor: "#D3D8E3",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#D3D8E3",
                            },
                          },
                        }}
                        size="small"
                        variant="outlined"
                        {...params}
                      />
                    )}
                  />
                  {changeModulesForm.touched.modules &&
                    Boolean(changeModulesForm.errors.modules) && (
                      <FormHelperText sx={{ color: "#d32f2f" }}>
                        {changeModulesForm.errors.modules}
                      </FormHelperText>
                    )}
                  <Grid
                    container
                    justifyContent="flex-end"
                    columnSpacing={2}
                    sx={{ mt: 4 }}
                  >
                    <CustomButton
                      text="Cancel"
                      type="secondary"
                      processing={false}
                      action={handleCloseUpdateModal}
                    />
                    <div style={{ width: "20px" }}></div>
                    <CustomButton
                      text="Send Module Change Request"
                      type="update"
                      processing={processing}
                      action={changeModulesForm.handleSubmit}
                      disabled={!modulesTouched}
                    />
                  </Grid>
                </>
              ) : (
                <ScreenLoader />
              )}
            </Box>
          </Modal>
          {/*Change key Modal  */}
          <Modal
            open={openChangeModal}
            onClose={handleCloseChangeModal}
            aria-labelledby="change-license"
            aria-describedby="change-license"
          >
            <Box sx={changeModalstyle}>
              <Box
                sx={{
                  bgcolor: "#050D2E",
                  height: "15%",
                  color: "#ffffff",
                  pr: 4,
                  pl: 4,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  id="change-license"
                  sx={{
                    fontSize: "1.25rem",
                    fontWeight: 550,
                    lineHeight: "24px",
                  }}
                >
                  License Key
                </Typography>
                <CloseIcon
                  sx={{ cursor: "pointer" }}
                  onClick={handleCloseChangeModal}
                />
              </Box>

              <Box
                sx={{
                  p: 4,
                  height: "57%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: ".813rem",
                    fontWeight: 500,
                    lineHeight: "15px",
                    width: "85%",
                    paddingBottom: "20px",
                  }}
                >
                  Enter the license key we shared with you to update the system
                  with the new modules’ changes you have requested.
                </Typography>

                <InputLabel
                  htmlFor="newKey"
                  sx={{
                    color: "#050D2E",
                    fontSize: ".875rem",
                    fontWeight: 550,
                    letterSpacing: "-0.5px",
                    lineHeight: "16px",
                    pb: 1,
                    pt: 1,
                  }}
                >
                  Your License Key
                </InputLabel>
                <OutlinedInput
                  id="newKey"
                  placeholder="00EG0000-0000-0000-00000000000"
                  sx={{
                    width: "360px",
                    height: "36px",
                    bgcolor: "#ffffff",
                    "& .MuiInputBase-root": { height: "36px" },
                    "& .MuiOutlinedInput": { height: "36px" },
                  }}
                  value={formik.values.newKey}
                  onChange={handleKeyChange}
                  error={Boolean(formik.errors.newKey)}
                />
                {Boolean(formik.errors.newKey) && (
                  <FormHelperText id="newKey" error={true}>
                    {formik.errors.newKey}
                  </FormHelperText>
                )}
              </Box>

              <Divider />
              <Box
                sx={{
                  pr: 4,
                  pl: 4,
                  height: "15%",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <CustomButton
                  type="secondary"
                  text="Cancel"
                  action={handleCloseChangeModal}
                />
                <div style={{ width: "10px" }}></div>
                <CustomButton
                  type="update"
                  text="Update License"
                  processing={processing}
                  disabled={!newKeyTouched}
                  action={formik.handleSubmit}
                />
              </Box>
            </Box>
          </Modal>

          {/*View History modal */}
          {isViewHistoryModalOpen && (
            <ViewHistoryModal
              handleClose={() => setIsViewHistoryModalOpen(false)}
              licenses={licenseHistory}
              loading={loadingLicenseHistory}
            />
          )}
        </>
      )}
    </div>
  );
};

export default License;
